import gsap from 'gsap';

import { congratulationsCoinRssNames, congratulationsPopupRssNames } from '../../../core/gameConstants';
import { amountFormatter } from '../../../core/helpers/amountFormatter';
import { DrawerBase } from '../../../core/abstraction/DrawerBase';
import { getTranslation, translationKeys } from '../../../core/translation/translation';
import { gameMobileConfig } from '../../../core/configs';
import { FONT_BALOO_NAME, GAME_SCENE } from '../../../core/constats';
import { bonusSpinResponseHelper } from '../../../services/helperService/BonusSpinResponseHelper';
import { popupHelper } from '../../../services/helperService/PopupHelper';
import { BONUS_RESPONSE, GAME_SCENE_STATE } from '../../../stateManagment/gameState';
import { soundService } from '../../../services/helperService/PixiSoundService';
import { debounce } from '../../../core/helpers/debounce';
import { gameService } from '../../../services/GameService';
import { playerService } from '../../../services/PlayerService';
import { userService } from '../../../services/helperService/UserService';
import { historyHelperService } from '../../../services/helperService/HistoryHelperService';
import { partnerService } from '../../../services/helperService/PartnerService';
import {spinButtonHelper} from "../../../services/helperService/SpinButtonHelper";
import {gameDrawer} from "../../../game/GameDrawer";

export class BaseCongratulationsPopup extends DrawerBase {
  getPopup({
    width = gameMobileConfig.width,
    parentWidth = gameMobileConfig.config.width,
    parentHeight = gameMobileConfig.config.height,
    onClick = () => null,
    percent = 20,
    scale = 1,
  } = {}) {
    const container = this.createContainer('BaseCongratulationsPopup');
    const popupContainer = this.createContainer('popupContainer');
    const background = this.createSprite(this.resources[congratulationsPopupRssNames.background]);
    const animation = this.createSpine(this.resources[congratulationsCoinRssNames.congratulationsCoin]);
    animation.state.setAnimation(0, 'animation', true);

    const all = this.createGraphics()
      .beginFill(0x000000)
      .drawRect(0, 0, parentWidth, parentHeight);

    container.visible = popupHelper.congratulationsPopupState;
    background.scale.set(width / background.width);
    background.anchor.set(.5, 1);

    const popupPositionY = parentHeight / 2 + background.height * percent / 100;
    this.setPosition(background, { x: parentWidth / 2, y: popupPositionY });
    const blackRect = this.createGraphics()
      .beginFill(0x000000)
      .drawRect(
        0,
        0,
        parentWidth,
        parentHeight,
      )
      .endFill();
    blackRect.alpha = .9;
    this.addSubscriber(popupHelper.subscribeCongratulationsPopup(isOpen => {
      if (isOpen) {
        container.visible = isOpen;
        this.setPosition(popupContainer, { y: -900 });
        gsap.to(blackRect, { duration: 1.5, ease: 'power1.out', alpha: .7 });
        gsap.to(popupContainer, { duration: 1.5, ease: 'elastic.out(.2, .1)', y: 0 });
      } else {
        gsap.to(blackRect, { duration: .5, ease: 'power1.in', alpha: 0 });
        gsap.to(popupContainer, {
          duration: .5, ease: 'power1.in', y: -800, onComplete: () => {
            container.visible = isOpen;
          },
        });
      }
    }, true));

    const title = new this.PIXI.Text(`${getTranslation(translationKeys.CONGRATULATIONS)}`, {
      fontSize: 50,
      fill: 0xf7e0b4,
      fontFamily: FONT_BALOO_NAME,
      align: 'center',
    });
    title.anchor.set(.5);


    const youHaveWon = new this.PIXI.Text(`${getTranslation(translationKeys.YOU_HAVE_WON)}`, {
      fontSize: 30,
      fill: 0xf7e0b4,
      fontFamily: FONT_BALOO_NAME,
      align: 'center',
    });
    youHaveWon.anchor.set(.5);
    const amountValue = bonusSpinResponseHelper.getData()?.totalWinAmount || '';
    const amount = new this.PIXI.Text(`${amountFormatter.format(amountValue)} ${partnerService.displayCurrency}`, {
      fontSize: 100,
      fill: 0xf7e0b4,
      fontFamily: FONT_BALOO_NAME,
      align: 'center',
    });
    amount.anchor.set(.5);

    this.addSubscriber(BONUS_RESPONSE.subscribe((data) => {
      if (data && data.totalWinAmount) {
        amount.text = `${amountFormatter.format(data.totalWinAmount)} ${partnerService.displayCurrency}`;
        if (amount.text.length >= 13) {
          amount.style.fontSize = 78;
        }
        if (amount.text.length >= 14) {
          amount.style.fontSize = 72;
        }

        if (amount.text.length < 13) {
          amount.style.fontSize = 81.5;
        }
      }
    }));

    const pressToContinue = new this.PIXI.Text(`${getTranslation(translationKeys.PRESS_ANYWHERE_TO_CONTINUE)}`, {
      fontSize: 25,
      fill: 0xf7e0b4,
      fontFamily: FONT_BALOO_NAME,
      align: 'center',
    });
    pressToContinue.anchor.set(.5);

    all.alpha = 0;
    this.setupButtonMode(all);
    this.registerClick(all, event => {
      event.stopPropagation();
      soundService.playSingleClick();
      onClick();
    })

    this.setPosition(title, { x: 0, y: -90 * scale });
    this.setPosition(youHaveWon, { x: 0, y: -40 * scale });
    this.setPosition(amount, { x: 0, y: 25 * scale});
    this.setPosition(pressToContinue, { x: 0, y: 100 * scale });
    this.setPosition(animation, { x: parentWidth / 2, y: popupPositionY + 100 * scale });
    title.scale.set(scale);
    youHaveWon.scale.set(scale);
    amount.scale.set(scale);
    pressToContinue.scale.set(scale);
    const textsContainer = this.createContainer('textsContainer');
    this.setPosition(textsContainer, {x: parentWidth / 2, y: popupPositionY - 250 * scale})
    textsContainer.addChild(
      title,
      youHaveWon,
      amount,
      pressToContinue,
    );

    animation.scale.set(width / animation.width * 1.2);
    popupContainer.addChild(
      background,
      animation,
      textsContainer,
    );

    container.addChild(
      blackRect,
      popupContainer,
      all,
    );
    return container;
  }

  onClick = () => debounce(() => {
      popupHelper.setCongratulationsPopupState(false);
      if (gameService.isRealMode) {
        playerService.getBalance();
      } else {
        userService.plusBalance(bonusSpinResponseHelper.getData()?.totalWinAmount);
      }
      historyHelperService.updateHistory();
      GAME_SCENE_STATE.next(GAME_SCENE.MAIN);
        spinButtonHelper.enable()
      }, 300, true,
  );
}
